'use client';

import Link from 'next/link';
import { ChevronLeft } from 'lucide-react';
import { DocContentWrapper } from '@/shared/components/docs/DocContentWrapper';
import SmsBestPracticesContent from '@/shared/components/docs/content/SmsBestPracticesContent';

export default function SmsBestPracticesPage() {
  return (
    <div className="bg-white">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100 py-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-4">
              <li>
                <Link href="/docs" className="text-brand hover:text-[#2a6bb0] font-medium">
                  Docs & Guides
                </Link>
              </li>
              <li>
                <div className="flex items-center">
                  <svg className="shrink-0 h-5 w-5 text-gray-300" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
                  </svg>
                  <span className="ml-4 text-sm font-medium text-gray-600">SMS Best Practices</span>
                </div>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="lg:grid lg:grid-cols-4 lg:gap-12">

          {/* Table of Contents - Sidebar */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-8">
              <h3 className="text-sm font-semibold tracking-wide uppercase text-gray-400 mb-4">On this page</h3>
              <nav className="space-y-3 border-l-2 border-gray-100 pl-4">
                <a href="#why-texting" className="block text-sm text-gray-600 hover:text-brand transition-colors">Why Texting Works</a>
                <a href="#ctia" className="block text-sm text-gray-600 hover:text-brand transition-colors">CTIA Guidelines</a>
                <a href="#timing" className="block text-sm text-gray-600 hover:text-brand transition-colors">Timing & Frequency</a>
                <a href="#content" className="block text-sm text-gray-600 hover:text-brand transition-colors">Message Content</a>
                <a href="#insurance-specific" className="block text-sm text-gray-600 hover:text-brand transition-colors">Insurance-Specific</a>
                <a href="#use-cases" className="block text-sm text-gray-600 hover:text-brand transition-colors">Common Use Cases</a>
                <a href="#documentation" className="block text-sm text-gray-600 hover:text-brand transition-colors">Documentation & E&O</a>
                <a href="#staff-training" className="block text-sm text-gray-600 hover:text-brand transition-colors">Staff Training</a>
                <a href="#voley-features" className="block text-sm text-gray-600 hover:text-brand transition-colors">How Voley Helps</a>
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">SMS Best Practices for Insurance Agencies</h1>
              <p className="text-xl text-gray-600">
                Practical guidance for effective, compliant text messaging that builds client relationships and protects your agency.
              </p>
              <div className="mt-4 flex items-center text-sm text-gray-500">
                <span>Estimated time: 12 minutes</span>
              </div>
            </div>

            <div className="bg-brand/10 border border-brand/20 rounded-2xl p-5 mb-8 [&>:first-child]:mt-0">
              <p className="text-sm text-gray-700 mb-0">
                <strong>Related Guides:</strong> This guide focuses on practical best practices. For legal requirements, see our <Link href="/docs/tcpa" className="text-brand hover:text-[#2a6bb0]">TCPA Compliance Guide</Link>. For carrier registration, see <Link href="/docs/compliance" className="text-brand hover:text-[#2a6bb0]">A2P 10DLC Compliance</Link>.
              </p>
            </div>

            {/* Content Sections */}
            <div className="prose prose-lg max-w-none">
              <DocContentWrapper variant="external">
                <SmsBestPracticesContent variant="external" />
              </DocContentWrapper>
            </div>

            {/* Footer Navigation */}
            <div className="mt-16 pt-8 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <Link
                  href="/docs"
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-gray-200 text-sm font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm transition-all group"
                >
                  <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
                  Back to Docs
                </Link>
                <div className="text-sm text-gray-500">
                  Need help? <Link href="/contact" className="text-brand hover:text-[#2a6bb0] font-medium">Contact our team</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
